"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import type { SwiperProps } from "swiper/react";
import { SearchX } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { AppSwiper, SwiperSlide } from "@/components/ui/swiper";
import { extractStrapiMediaUrl, type StrapiEntity } from "@/lib/strapi";
import { cn } from "@/lib/utils";
import type { ProductAttributes } from "@/services/product";

export type ProductCarouselItem = {
  key: string;
  href: string;
  title: string;
  headerText: string;
  imageUrl: string | null;
  imageAlt: string;
  categorySlug?: string | null;
};

function coerceSizeLabel(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return `${value}ml`;
  if (typeof value !== "string") return String(value);
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function parseDateMs(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

type ProductCategoryValue = {
  id?: number;
  slug?: string | null;
  name?: string | null;
  LabelSelect?: string | null;
} | null;

function getCategoryKey(category: ProductCategoryValue): string | null {
  if (typeof category?.id === "number") return `id:${category.id}`;
  if (category?.slug) return `slug:${category.slug}`;
  if (category?.name) return `name:${category.name}`;
  if (category?.LabelSelect) return `label:${category.LabelSelect}`;
  return null;
}

function getCategoryHeaderText(category: ProductCategoryValue): string {
  return category?.name ?? category?.LabelSelect ?? category?.slug ?? "Product";
}

function getProductRecencyScore(product: StrapiEntity<ProductAttributes>): number {
  const attributes = product.attributes as {
    updatedAt?: unknown;
    createdAt?: unknown;
  };

  const updatedAtMs = parseDateMs(attributes.updatedAt);
  if (updatedAtMs !== null) return updatedAtMs;

  const createdAtMs = parseDateMs(attributes.createdAt);
  return createdAtMs ?? 0;
}

export function buildProductCarouselItems(
  strapiProducts: Array<StrapiEntity<ProductAttributes>>,
): ProductCarouselItem[] {
  const categoryOrder: string[] = [];
  const selectedByCategory = new Map<string, StrapiEntity<ProductAttributes>>();
  const withoutCategory: typeof strapiProducts = [];

  for (const product of strapiProducts) {
    const attributes = product.attributes as {
      product_category?: ProductCategoryValue;
    };

    const category = attributes.product_category ?? null;
    const categoryKey = getCategoryKey(category);

    if (!categoryKey) {
      withoutCategory.push(product);
      continue;
    }

    const existing = selectedByCategory.get(categoryKey);
    if (!existing) {
      categoryOrder.push(categoryKey);
      selectedByCategory.set(categoryKey, product);
      continue;
    }

    const nextScore = getProductRecencyScore(product);
    const existingScore = getProductRecencyScore(existing);

    if (nextScore > existingScore || (nextScore === existingScore && product.id > existing.id)) {
      selectedByCategory.set(categoryKey, product);
    }
  }

  const filtered: typeof strapiProducts = [
    ...categoryOrder.map((key) => selectedByCategory.get(key)).filter(Boolean),
    ...withoutCategory,
  ] as typeof strapiProducts;

  return filtered.map((product) => {
    const attributes = product.attributes as {
      slug?: string | null;
      name?: string | null;
      size?: unknown;
      product_category?: ProductCategoryValue;
      cover_image?: unknown;
    };

    const slug = attributes.slug ?? String(product.id);
    const title = attributes.name ?? slug;
    const headerText = getCategoryHeaderText(attributes.product_category ?? null);
    const categorySlug =
      typeof (attributes.product_category as { slug?: string | null } | null)?.slug === "string"
        ? (attributes.product_category as { slug?: string | null }).slug
        : null;

    const imageUrl = extractStrapiMediaUrl(attributes.cover_image);
    const sizeLabel = coerceSizeLabel(attributes.size);
    const href = sizeLabel ? `/products/${slug}?size=${encodeURIComponent(sizeLabel)}` : `/products/${slug}`;

    return {
      key: slug,
      href,
      title,
      headerText,
      imageUrl,
      imageAlt: title,
      categorySlug,
    };
  });
}

export function ProductPreviewCard({
  item,
  imageSizes = "(min-width: 1280px) 360px, (min-width: 768px) 320px, 80vw",
}: {
  item: ProductCarouselItem;
  imageSizes?: string;
}) {
  const isRemoteImage = typeof item.imageUrl === "string" && /^https?:\/\//i.test(item.imageUrl);

  return (
    <Link href={item.href} className="block focus:outline-none">
      <Card
        data-product-card
        className="group flex h-full flex-col overflow-hidden rounded-3xl border-none bg-foreground transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="grid h-12 place-items-center px-4 text-[clamp(13px,1.2vw,18px)] font-normal tracking-[0.12em] text-background">
          <p className="w-full truncate text-center">{item.headerText}</p>
        </div>
        <CardContent className="flex-1 rounded-t-3xl bg-[#F3F4F7]">
          <div className="aspect-4/5 w-full overflow-hidden">
            <div className="grid h-full w-full place-items-center">
              {item.imageUrl ? (
                <Image
                  src={item.imageUrl}
                  alt={item.imageAlt}
                  width={280}
                  height={420}
                  quality={90}
                  className="h-full max-h-[clamp(240px,55vw,360px)] w-auto object-contain drop-shadow-[0_18px_18px_rgba(0,0,0,0.18)]"
                  draggable={false}
                  sizes={imageSizes}
                  unoptimized={isRemoteImage}
                />
              ) : (
                <div className="px-5 text-center">
                  <p className="text-sm font-semibold tracking-[0.08em] text-foreground">{item.title}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function normalizeText(value: string): string {
  return value.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

function getItemSize(item: ProductCarouselItem): string | null {
  const qIndex = item.href.indexOf("?");
  if (qIndex < 0) return null;
  const params = new URLSearchParams(item.href.slice(qIndex + 1));
  const size = params.get("size");
  if (size && size.trim()) return size.trim().toLowerCase();
  const inTitle = normalizeText(item.title);
  const match = inTitle.match(/\b(\d{2,3})\s*ml\b/);
  if (match) return `${match[1]}ml`.toLowerCase();
  return null;
}

function getDefaultOrderRank(item: ProductCarouselItem): number {
  const category = normalizeText(item.headerText);
  const size = getItemSize(item);
  const slug = (item.categorySlug ?? "").toLowerCase();

  if (slug === "extrait-de-parfum-rmn" || category.includes("extrait")) return 1;
  if (slug === "eau-de-toilette-100ml" || ((category.includes("toilette") || category.includes("edt")) && size === "100ml"))
    return 2;
  if (slug === "eau-de-parfum-rmn" || ((category.includes("parfum") || category.includes("edp")) && size === "50ml"))
    return 3;
  if (slug === "roll-on" || ((category.includes("deodorant") || category.includes("deo")) && (category.includes("roll on") || category.includes("rollon") || category.includes("roll-on"))))
    return 4;
  return 9999;
}

function sortProductCarouselItems(items: ProductCarouselItem[]): ProductCarouselItem[] {
  return items
    .map((item, index) => ({ item, index, rank: getDefaultOrderRank(item) }))
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}

export function ProductCardsCarousel({
  items,
  isLoading,
  emptyTitle = "No products found",
  emptyDescription = "Try again later.",
  slideClassName = "h-auto",
  ...swiperProps
}: Omit<SwiperProps, "children"> & {
  items: ProductCarouselItem[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  slideClassName?: string;
  className?: string;
  swiperClassName?: string;
  showNavigation?: boolean;
}) {
  if (isLoading) {
    return (
      <AppSwiper
        {...swiperProps}
        showNavigation={false}
        className={cn(swiperProps.className, "animate-pulse")}
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <SwiperSlide key={`product-skeleton-${index}`} className={slideClassName}>
            <Card
              data-product-card
              className="flex h-full flex-col overflow-hidden rounded-3xl border-none bg-foreground"
            >
              <div className="grid h-12 place-items-center px-4">
                <div className="h-3 w-24 rounded-lg bg-white/15" />
              </div>
              <CardContent className="flex-1 rounded-t-3xl bg-[#F3F4F7]">
                <div className="aspect-4/5 w-full overflow-hidden px-5 pt-4">
                  <div className="h-full w-full rounded-2xl bg-muted" />
                </div>
                <div className="px-5 pb-6 pt-3">
                  <div className="h-5 w-[80%] rounded-lg bg-muted" />
                </div>
              </CardContent>
            </Card>
          </SwiperSlide>
        ))}
      </AppSwiper>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className={cn(
          "flex min-h-[clamp(220px,40vw,320px)] w-full items-center justify-center rounded-3xl border border-border bg-muted/30 px-6 py-10 text-center",
          swiperProps.className,
        )}
      >
        <div className="mx-auto flex max-w-sm flex-col items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-background/60 ring-1 ring-border">
            <SearchX className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-semibold tracking-tight text-foreground">{emptyTitle}</p>
          <p className="text-sm text-muted-foreground">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <AppSwiper {...swiperProps}>
      {items.map((item) => (
        <SwiperSlide key={item.key} className={slideClassName}>
          <ProductPreviewCard item={item} />
        </SwiperSlide>
      ))}
    </AppSwiper>
  );
}
